import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ZoomService {
    private readonly STORAGE_KEY = 'zoom_level';
    private readonly MIN_ZOOM = 50;
    private readonly MAX_ZOOM = 150;
    private readonly ZOOM_STEP = 5;
    private readonly WHEEL_SENSITIVITY = 0.15;

    private containerElement: HTMLElement | null = null;
    private contentElement: HTMLElement | null = null;
    private accumulatedDelta = 0;
    private wheelTimeout: any = null;
    private zoomLevel = this.getInitialZoomLevel();
    private lastDistance = 0;
    private isPinching = false;
    private lastCenter = { x: 0, y: 0 };

    public zoomPercentage$ = new BehaviorSubject<number>(this.zoomLevel);
    public zoomChange$ = new Subject<void>();
    public persistZoom = true;

    private getInitialZoomLevel(): number {
        const storedValue = localStorage.getItem(this.STORAGE_KEY);
        return storedValue ? Number(storedValue) : 100;
    }

    private persistZoomLevel(value: number): void {
        localStorage.setItem(this.STORAGE_KEY, value.toString());
    }

    initializeZoom(containerSelector: string, contentSelector: string, zoomLevel?: number, transformOrigin?: string, persistZoom: boolean = true) {
        this.containerElement = document.querySelector(containerSelector);
        this.contentElement = document.querySelector(contentSelector);

        if (!this.containerElement || !this.contentElement) {
            console.error('Container or content elements not found');
            return;
        }

        if (this.contentElement) {
            this.contentElement.style.transformOrigin =  transformOrigin ? transformOrigin : '0% 0%';
            this.persistZoom = persistZoom;
            zoomLevel && this.applyZoom(zoomLevel)
            this.contentElement.style.transform = `scale(${this.zoomLevel / 100})`;
        }

        this.setupEventListeners();
    }

    private setupEventListeners() {
        if (!this.contentElement) return;

        this.contentElement.addEventListener('touchstart', this.handleTouchStart.bind(this));
        this.contentElement.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
        this.contentElement.addEventListener('touchend', this.handleTouchEnd.bind(this));
        this.contentElement.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
    }

    private isEventInBounds(e: MouseEvent | Touch): boolean {
        if (!this.contentElement) return false;
        const rect = this.contentElement.getBoundingClientRect();
        return (
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
        );
    }

    private handleTouchStart(e: TouchEvent) {
        if (e.touches.length !== 2) return;

        if (!this.isEventInBounds(e.touches[0]) || !this.isEventInBounds(e.touches[1])) return;

        e.preventDefault();
        this.isPinching = true;

        const touch1 = e.touches[0];
        const touch2 = e.touches[1];

        this.lastDistance = this.getDistance(touch1, touch2);
        this.lastCenter = this.getCenter(touch1, touch2);
    }

    private handleTouchMove(e: TouchEvent) {
        if (!this.isPinching || e.touches.length !== 2) return;

        if (!this.isEventInBounds(e.touches[0]) || !this.isEventInBounds(e.touches[1])) return;

        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];

        const currentDistance = this.getDistance(touch1, touch2);
        const scale = currentDistance / this.lastDistance;

        const zoomChange = (scale - 1) * 100;
        const potentialNewZoom = this.zoomLevel + zoomChange;

        if (this.zoomLevel === this.MIN_ZOOM && potentialNewZoom < this.MIN_ZOOM) {
            return;
        }

        const newZoom = this.clampZoom(potentialNewZoom);

        this.applyZoom(newZoom);
        this.lastDistance = currentDistance;
    }

    private handleTouchEnd() {
        this.isPinching = false;
    }

    private handleWheel(e: WheelEvent) {
        if (!e.ctrlKey || !this.isEventInBounds(e)) return;

        e.preventDefault();
        e.stopPropagation();

        if (this.wheelTimeout) {
            clearTimeout(this.wheelTimeout);
        }

        this.accumulatedDelta += e.deltaY * this.WHEEL_SENSITIVITY;

        if (Math.abs(this.accumulatedDelta) >= 1) {
            const zoomDelta = -Math.sign(this.accumulatedDelta) * this.ZOOM_STEP;
            const potentialNewZoom = this.zoomLevel + zoomDelta;

            this.accumulatedDelta = 0;

            if (this.zoomLevel === this.MIN_ZOOM && potentialNewZoom < this.MIN_ZOOM) {
                return;
            }

            const newZoom = this.clampZoom(potentialNewZoom);
            this.applyZoom(newZoom);
        }

        this.wheelTimeout = setTimeout(() => {
            this.accumulatedDelta = 0;
        }, 150);
    }

    private updateTransformOrigin(x: number, y: number) {
        if (this.contentElement) {
            const boundedX = Math.max(0, Math.min(100, x));
            const boundedY = Math.max(0, Math.min(100, y));
            this.contentElement.style.transformOrigin = `${boundedX}% ${boundedY}%`;
        }
    }

    private clampZoom(zoom: number): number {
        return Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, zoom));
    }

    private applyZoom(newZoom: number) {
        if (!this.contentElement) return;

        if (this.zoomLevel !== newZoom) {
            this.zoomLevel = newZoom;
            this.persistZoom && this.persistZoomLevel(this.zoomLevel);
            this.zoomPercentage$.next(this.zoomLevel);
            this.contentElement.style.transform = `scale(${this.zoomLevel / 100})`;

            requestAnimationFrame(() => {
                this.zoomChange$.next();
            });
        }
    }

    private getDistance(touch1: Touch, touch2: Touch): number {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    private getCenter(touch1: Touch, touch2: Touch) {
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
        };
    }

    decreaseZoom() {
        if (this.zoomLevel === this.MIN_ZOOM) {
            return;
        }
        const newZoom = this.clampZoom(this.zoomLevel - this.ZOOM_STEP);
        this.applyZoom(newZoom);
    }

    increaseZoom() {
        const newZoom = this.clampZoom(this.zoomLevel + this.ZOOM_STEP);
        this.applyZoom(newZoom);
    }

    resetZoom() {
        this.updateTransformOrigin(0, 0);
        this.applyZoom(100);
    }
}